import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Button from "../../../components/Button/Button";

import { getSettings } from "../../../services/settingsServices";
import { getLexique } from "../../../services/lexiqueServices";
import { getLexiqueKarate } from "../../../services/lexiquekarateServices";

import LexiqueToolbar from "./LexiqueToolbar";
import LexiqueRow from "./LexiqueRow";
import LexiqueSkeleton from "./LexiqueSkeleton";
import EmptyState from "./EmptyState";

// --- UI par défaut ---
const DEFAULT_UI = {
  q: "",
  letter: "Toutes",
  sort: "jp", // jp | fr
  favOnly: false,
};

const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });
const strip = (s = "") =>
  s.toString().normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();

// clé unique locale pour favoris
const FAV_KEY = "lexique_favorites";

export default function LexiquePage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [discipline, setDiscipline] = useState("");
  const [data, setData] = useState([]); // [{japonais, traduction}]
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // UI state persisté
  const [ui, setUi] = useState(() => {
    const saved = localStorage.getItem("lexique_ui_state");
    return saved ? { ...DEFAULT_UI, ...JSON.parse(saved) } : DEFAULT_UI;
  });
  const [debouncedQ, setDebouncedQ] = useState(ui.q);
  const [shareCopied, setShareCopied] = useState(false);

  // Favoris (localStorage)
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(FAV_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem("lexique_ui_state", JSON.stringify(next));
  }, []);

  // debounce recherche
  useEffect(() => {
    const t = setTimeout(() => persistUi({ ...ui, q: debouncedQ }), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [debouncedQ]);

  // Fetch settings + data
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const settings = await getSettings();
        const d = settings?.[0]?.discipline_nom || "Judo";
        setDiscipline(d);
        const src = d === "Karate" ? getLexiqueKarate : getLexique;
        const raw = await src();
        const safe = Array.isArray(raw) ? raw : [];
        setData(safe);
      } catch (e) {
        console.error(e);
        setError("Impossible de charger le lexique pour le moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Alphabet disponible (en fonction des données)
  const availableLetters = useMemo(() => {
    const set = new Set(
      data.map((x) => (x?.japonais || "").toUpperCase().charAt(0)).filter(Boolean)
    );
    const letters = Array.from(set).filter((c) => /[A-ZÀ-ÖØ-Ý]/i.test(c)); // sécurise
    letters.sort((a, b) => collatorFR.compare(a, b));
    return ["Toutes", ...letters];
  }, [data]);

  // Filtrage + tri
  const filtered = useMemo(() => {
    const q = strip(ui.q);
    const letter = ui.letter;

    let arr = data.filter((row) => {
      const jp = row.japonais || "";
      const fr = row.traduction || "";
      const matchesQ =
        !q ||
        strip(jp).includes(q) ||
        strip(fr).includes(q);
      const matchesLetter =
        letter === "Toutes" || (jp.toUpperCase().startsWith(letter.toUpperCase()));

      const matchesFav = !ui.favOnly || favorites.includes(jp);

      return matchesQ && matchesLetter && matchesFav;
    });

    arr.sort((a, b) => {
      if (ui.sort === "fr") {
        return collatorFR.compare(String(a.traduction), String(b.traduction))
            || collatorFR.compare(String(a.japonais), String(b.japonais));
      }
      // jp par défaut
      return collatorFR.compare(String(a.japonais), String(b.japonais))
          || collatorFR.compare(String(a.traduction), String(b.traduction));
    });

    return arr;
  }, [data, ui, favorites]);

  // Groupes par lettre (pour affichage avec headers sticky)
  const groups = useMemo(() => {
    const map = new Map();
    const takeLetter = (jp = "") => (jp ? jp.toUpperCase().charAt(0) : "#");
    filtered.forEach((row) => {
      const k = ui.letter === "Toutes" ? takeLetter(row.japonais) : ui.letter;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(row);
    });
    // tri des sections par ordre alpha FR
    const keys = Array.from(map.keys()).sort((a, b) => collatorFR.compare(a, b));
    return keys.map((k) => ({ letter: k, items: map.get(k) }));
  }, [filtered, ui.letter]);

  // Actions UI
  const reset = () => persistUi(DEFAULT_UI);

  const copyShare = async () => {
    try {
      const params = new URLSearchParams();
      if (ui.q) params.set("q", ui.q);
      if (ui.letter !== "Toutes") params.set("letter", ui.letter);
      if (ui.sort !== DEFAULT_UI.sort) params.set("sort", ui.sort);
      if (ui.favOnly) params.set("fav", "1");
      const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1200);
    } catch {}
  };

  const toggleFavorite = (jp) => {
    setFavorites((prev) => {
      const next = prev.includes(jp) ? prev.filter((x) => x !== jp) : [...prev, jp];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Export CSV (version filtrée)
  const exportCSV = () => {
    const rows = [["japonais", "traduction"], ...filtered.map(({ japonais, traduction }) => [japonais, traduction])];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lexique_${discipline || "judo"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section className="w-full">
      <ScrollIndicator />
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title={`Lexique ${discipline || "Judo"}`}
          align="center"
          className="mb-1"
        />

        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte globale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Header gradient */}
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold">Lexique {discipline || "Judo"}</h2>
                <p className="text-white/90 text-sm">
                  Recherche, tri, favoris, export CSV — présentation groupée par lettres.
                </p>
              </div>
              <div className="text-sm">
                <span className="font-semibold">{filtered.length}</span> résultat(s)
                {ui.favOnly && (
                  <span className="ml-2 opacity-90">
                    — Favoris : <span className="font-semibold">{favorites.length}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800">
            <LexiqueToolbar
              ui={ui}
              availableLetters={availableLetters}
              onChange={(patch) => persistUi({ ...ui, ...patch })}
              debouncedQ={debouncedQ}
              setDebouncedQ={setDebouncedQ}
              onReset={reset}
              onShare={copyShare}
              shareCopied={shareCopied}
              onExport={exportCSV}
            />
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {loading && (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <LexiqueSkeleton key={i} />
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="text-center py-10 text-slate-600 dark:text-slate-300">{error}</div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <EmptyState message="Aucun terme ne correspond à votre recherche." />
            )}

            {!loading && !error && filtered.length > 0 && (
              <div className="divide-y divide-slate-200/70 dark:divide-slate-800/70">
                {groups.map(({ letter, items }) => (
                  <section key={letter}>
                    <header className="sticky top-[150px] -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-white/80 dark:bg-slate-900/60 backdrop-blur z-10">
                      <h4 className="text-xs font-bold tracking-widest uppercase text-slate-500 dark:text-slate-400">
                        {letter}
                      </h4>
                    </header>
                    <ul className="divide-y divide-slate-200/70 dark:divide-slate-800/70">
                      {items.map((row) => (
                        <LexiqueRow
                          key={`${row.japonais}-${row.traduction}`}
                          row={row}
                          q={ui.q}
                          isFav={favorites.includes(row.japonais)}
                          toggleFav={() => toggleFavorite(row.japonais)}
                        />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
